export type CatalogSourceType = 'official' | 'partner' | 'custom';

export interface SoftwareCatalogItem {
  id: string;
  name: string;
  provider: string;
  description: string;
  sourceType: CatalogSourceType;
  categories: string[];
  architectures: string[];
  installed?: boolean;
  updateAvailable?: boolean;
  /** Optional logo key: 'redhat' | 'ibm' (defaults to redhat) */
  logo?: 'redhat' | 'ibm';
}

export interface CatalogFilterOption {
  id: string;
  label: string;
  support?: string;
}

export interface CatalogFilterSection {
  id: string;
  label: string;
  options: CatalogFilterOption[];
}

export const categoryFilterSections: CatalogFilterSection[] = [
  {
    id: 'infrastructure',
    label: 'Infrastructure',
    options: [
      { id: 'monitoring', label: 'Monitoring' },
      { id: 'networking', label: 'Networking' },
      { id: 'security', label: 'Security' },
      { id: 'platform', label: 'Platform services' },
    ],
  },
  {
    id: 'operating-systems',
    label: 'Operating systems',
    options: [
      { id: 'rhel', label: 'Red Hat Enterprise Linux' },
      { id: 'centos', label: 'CentOS Stream' },
      { id: 'fedora', label: 'Fedora' },
    ],
  },
];

export const architectureFilterOptions: CatalogFilterOption[] = [
  { id: 'x86_64', label: 'Intel and AMD 64-bit', support: 'Full support' },
  { id: 'aarch64', label: 'ARM 64-bit', support: 'Full support' },
  { id: 'ppc64le', label: 'Power 64-bit', support: 'Limited support' },
  { id: 's390x', label: 'IBM Z', support: 'Community support' },
];

export const infrastructureCategoryIds = categoryFilterSections[0].options.map((option) => option.id);
export const operatingSystemCategoryIds = categoryFilterSections[1].options.map((option) => option.id);

export const mockSoftwareCatalogItems: SoftwareCatalogItem[] = [
  {
    id: 'ibm-edge-computing',
    name: 'IBM Edge Application Manager',
    provider: 'IBM',
    description:
      'Autonomous management for edge computing workloads. Deploy, monitor, and scale AI and analytics applications across distributed edge nodes with IBM Computing support.',
    sourceType: 'custom',
    categories: ['applications', 'infrastructure', 'platform', 'monitoring'],
    architectures: ['x86_64', 'aarch64', 's390x', 'ppc64le'],
    installed: false,
    logo: 'ibm',
  },
  {
    id: 'rhel-9',
    name: 'Red Hat Enterprise Linux',
    provider: 'Red Hat',
    description:
      'Enterprise-grade Linux platform for edge deployments with long-term support, security updates, and certified hardware compatibility.',
    sourceType: 'official',
    categories: ['operating-systems', 'rhel'],
    architectures: ['x86_64', 'aarch64'],
    installed: true,
  },
  {
    id: 'edge-observability',
    name: 'Edge Observability',
    provider: 'Approved Company Inc.',
    description:
      'Collect metrics and logs from edge devices with lightweight agents designed for intermittent connectivity and limited bandwidth.',
    sourceType: 'partner',
    categories: ['applications', 'monitoring'],
    architectures: ['x86_64'],
    installed: false,
  },
  {
    id: 'fleet-sync',
    name: 'Fleet Sync Agent',
    provider: 'Approved Company Inc.',
    description:
      'Synchronize configuration and software state across distributed fleets with policy-driven rollout and rollback support.',
    sourceType: 'partner',
    categories: ['applications', 'infrastructure'],
    architectures: ['x86_64', 'aarch64'],
    installed: false,
  },
  {
    id: 'secure-bootkit',
    name: 'Secure Bootkit',
    provider: 'Approved Company Inc.',
    description:
      'Harden edge boot chains with measured boot, attestation, and integration with your existing certificate authority.',
    sourceType: 'custom',
    categories: ['security', 'infrastructure'],
    architectures: ['x86_64'],
    installed: true,
    updateAvailable: true,
  },
  {
    id: 'container-runtime',
    name: 'Container Runtime',
    provider: 'Approved Company Inc.',
    description:
      'OCI-compliant runtime optimized for resource-constrained edge nodes with automatic garbage collection and image pruning.',
    sourceType: 'custom',
    categories: ['applications', 'infrastructure'],
    architectures: ['x86_64', 'aarch64', 'ppc64le'],
    installed: false,
  },
  {
    id: 'rhel-edge',
    name: 'RHEL for Edge',
    provider: 'Red Hat',
    description:
      'Purpose-built RHEL image streams for remote edge sites with atomic updates and managed lifecycle for unattended devices.',
    sourceType: 'official',
    categories: ['operating-systems', 'rhel'],
    architectures: ['x86_64', 'aarch64'],
    installed: true,
  },
  {
    id: 'network-manager',
    name: 'Network Manager',
    provider: 'Approved Company Inc.',
    description:
      'Configure WAN, LTE, and Wi-Fi interfaces on edge gateways with zero-touch provisioning and centralized policy templates.',
    sourceType: 'partner',
    categories: ['networking', 'infrastructure'],
    architectures: ['x86_64'],
    installed: false,
  },
  {
    id: 'microshift',
    name: 'MicroShift',
    provider: 'Red Hat',
    description:
      'Single-node OpenShift for edge workloads. Run containerized applications close to data sources with Kubernetes APIs.',
    sourceType: 'official',
    categories: ['applications', 'infrastructure'],
    architectures: ['x86_64', 'aarch64'],
    installed: false,
  },
];

export const sourceTypeLabels: Record<CatalogSourceType, string> = {
  official: 'Official',
  partner: 'Partner',
  custom: 'Custom',
};
